/**
 * Online Food Ordering Theme Configuration
 * Design with warm reds/oranges
 */

import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';

// Helper function to lighten/darken hex color
function adjustColor(color: string, amount: number): string {
  const num = parseInt(color.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// Get brand colors with optional primary color override
const getBrandColors = (primaryColor?: string) => ({
  primary: {
    main: primaryColor || '#E53935', // Warm red (default)
    light: primaryColor ? adjustColor(primaryColor, 40) : '#FF6F60',
    dark: primaryColor ? adjustColor(primaryColor, -40) : '#AB000D',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#FF6D00', // Warm orange
    light: '#FF9E40',
    dark: '#C43C00',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#2E7D32',
    light: '#4CAF50',
    dark: '#1B5E20',
  },
  warning: {
    main: '#ED6C02',
    light: '#FF9800',
    dark: '#E65100',
  },
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
  },
});

// Common theme options
const commonThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '1.125rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: '20px 0 0 20px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
};

// Create light theme with optional primary color and direction
const createLightTheme = (primaryColor?: string, direction: 'ltr' | 'rtl' = 'ltr') =>
  createTheme({
    ...commonThemeOptions,
    direction,
    palette: {
      mode: 'light',
      ...getBrandColors(primaryColor),
      background: {
        default: '#FAFAFA',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#1A1A1A',
        secondary: '#666666',
      },
      divider: alpha('#000000', 0.08),
    },
  });

// Create dark theme with optional primary color and direction
const createDarkTheme = (primaryColor?: string, direction: 'ltr' | 'rtl' = 'ltr') =>
  createTheme({
    ...commonThemeOptions,
    direction,
    palette: {
      mode: 'dark',
      ...getBrandColors(primaryColor),
      background: {
        default: '#121212',
        paper: '#1E1E1E',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#B0B0B0',
      },
      divider: alpha('#FFFFFF', 0.12),
    },
    components: {
      ...commonThemeOptions.components,
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: '#1E1E1E',
          },
        },
      },
    },
  });

// Export type for theme mode
export type ThemeMode = 'light' | 'dark';

// Get theme by mode, optional primary color, and direction
export const getTheme = (
  mode: ThemeMode,
  primaryColor?: string,
  direction: 'ltr' | 'rtl' = 'ltr'
) =>
  mode === 'light'
    ? createLightTheme(primaryColor, direction)
    : createDarkTheme(primaryColor, direction);
